import React from 'react';
import { cn } from '../../../lib/utils';

interface ThemeInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    variant?: 'default' | 'filled' | 'outlined' | 'ghost' | 'accent';
    size?: 'sm' | 'md' | 'lg';
}

const ThemeInput = React.forwardRef<HTMLInputElement, ThemeInputProps>(
    ({ className, variant = 'default', size = 'md', type, ...props }, ref) => {
        const baseClasses = "flex w-full rounded-md px-3 py-2 text-sm transition-all file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

        const variants = {
            default: "bg-[hsl(var(--input-bg))] text-[hsl(var(--input-fg))] border border-[hsl(var(--input-border))] placeholder:text-[hsl(var(--input-placeholder))] hover:border-[hsl(var(--input-hover-border))] focus-visible:border-[hsl(var(--input-focus-border))] focus-visible:ring-[hsl(var(--input-focus-ring))]",
            filled: "bg-[hsl(var(--muted))] text-[hsl(var(--input-fg))] border border-[hsl(var(--input-border))] placeholder:text-[hsl(var(--input-placeholder))] hover:bg-[hsl(var(--accent))] focus-visible:border-[hsl(var(--input-focus-border))] focus-visible:ring-[hsl(var(--input-focus-ring))]",
            outlined: "bg-transparent text-[hsl(var(--input-fg))] border-2 border-[hsl(var(--primary))] placeholder:text-[hsl(var(--input-placeholder))] hover:border-[hsl(var(--input-hover-border))] focus-visible:border-[hsl(var(--input-focus-border))] focus-visible:ring-[hsl(var(--input-focus-ring))]",
            ghost: "bg-transparent text-[hsl(var(--input-fg))] border-0 placeholder:text-[hsl(var(--input-placeholder))] hover:bg-[hsl(var(--muted))] focus-visible:ring-[hsl(var(--input-focus-ring))]",
            accent: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border border-[hsl(var(--accent))] placeholder:text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--primary))] focus-visible:border-[hsl(var(--primary))] focus-visible:ring-[hsl(var(--input-focus-ring))]"
        };

        const sizes = {
            sm: "h-[calc(2rem*var(--density-scale))] px-[calc(0.5rem*var(--density-scale))] text-[calc(0.75rem*var(--density-scale))]",
            md: "h-[calc(2.5rem*var(--density-scale))] px-[calc(0.75rem*var(--density-scale))] text-[calc(0.875rem*var(--density-scale))]",
            lg: "h-[calc(3rem*var(--density-scale))] px-[calc(1rem*var(--density-scale))] text-[calc(1rem*var(--density-scale))]"
        };

        return (
            <input
                type={type}
                className={cn(baseClasses, variants[variant], sizes[size], className)}
                ref={ref}
                {...props}
            />
        );
    }
);

ThemeInput.displayName = "ThemeInput";

export { ThemeInput };
